import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AlertCircle } from "lucide-react";

/**
 * Authenticated blob preview for tracks that may not be publicly streamable
 * (i.e., pending or rejected submissions). Uses axios with Authorization header,
 * loads the full file into a Blob URL, and renders <video> or <audio> accordingly.
 */
export default function AuthMediaPreview({ trackId, mediaType, testIdPrefix = "auth-preview" }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [unsupported, setUnsupported] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let revoked = false;
    let createdUrl = null;
    setError(null);
    setBlobUrl(null);
    setUnsupported(false);
    setLoading(true);

    (async () => {
      try {
        const resp = await api.get(`/tracks/${trackId}/stream`, { responseType: "blob" });
        if (revoked) return;
        const ct = resp.data?.type || resp.headers?.["content-type"] || "";
        const blob = ct ? new Blob([resp.data], { type: ct }) : resp.data;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[Yahweh AdminPreview] failed", { trackId, status: e?.response?.status, data: e?.response?.data });
        if (!revoked) setError("Could not load preview. Please refresh and try again.");
      } finally {
        if (!revoked) setLoading(false);
      }
    })();

    return () => {
      revoked = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [trackId]);

  const handleMediaError = () => {
    setUnsupported(true);
  };

  if (error) {
    return (
      <div className="text-xs text-red-400 mt-3 flex items-start gap-1" data-testid={`${testIdPrefix}-error`}>
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} /> {error}
      </div>
    );
  }
  if (loading || !blobUrl) {
    return <div className="text-xs text-[#6C707A] mt-3" data-testid={`${testIdPrefix}-loading`}>Loading preview…</div>;
  }
  if (unsupported) {
    return (
      <div className="text-xs text-amber-300 mt-3 flex items-start gap-1.5 p-2 rounded border border-amber-500/30 bg-amber-500/5" data-testid={`${testIdPrefix}-unsupported`}>
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        {mediaType === "video"
          ? "This MP4 was uploaded, but the browser cannot play this video format. Please ask the uploader to re-export as H.264/AAC MP4 or MP3."
          : "This audio file format isn't playable in this browser. MP3, M4A, or WAV are recommended."}
      </div>
    );
  }
  if (mediaType === "video") {
    return (
      <video
        controls
        src={blobUrl}
        onError={handleMediaError}
        playsInline
        preload="metadata"
        className="w-full mt-3 rounded-md max-h-96 bg-black"
        data-testid={`${testIdPrefix}-video`}
      />
    );
  }
  return (
    <audio
      controls
      src={blobUrl}
      onError={handleMediaError}
      preload="metadata"
      className="w-full mt-3"
      data-testid={`${testIdPrefix}-audio`}
    />
  );
}
